import { useSavedRecipe } from '@hooks/recipe/useSavedRecipe';
import { useSaveRecipe } from '@hooks/recipe/useSaveRecipe';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import SaveIcon from '@repo/ui/src/components/Icons/SaveIcon';
import { useState, useEffect } from 'react';
import { useCustomer } from '@hooks/customer';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import { classNames } from '@repo/ui/src/utils/classNames';

interface SaveButtonProps {
  contentId: string;
  className?: string;
}

const SaveButton = ({ contentId, className }: SaveButtonProps) => {
  const { t } = useTranslation();
  const { data: user } = useCustomer();
  const { push } = useModal(ModalID.login);
  const { isSaved, isLoading } = useSavedRecipe({ slotId: contentId });
  const { trigger: toggleSave, isMutating } = useSaveRecipe({ contentId });
  const [isRecipeSaved, setIsRecipeSaved] = useState(isSaved || false);

  const handleClick = async () => {
    if (!user) {
      push();
    } else {
      await toggleSave();
      setIsRecipeSaved(!isRecipeSaved);
    }
  };

  useEffect(() => {
    setIsRecipeSaved(isSaved);
  }, [isSaved]);

  return (
    <Button
      variant={ButtonVariant.secondary}
      size="none"
      className={classNames(className, 'tw-py-2')}
      onClick={handleClick}
      disabled={isMutating || isLoading}
      data-testid="save-button"
    >
      <div className="tw-flex tw-justify-center tw-gap-2">
        <div className="tw-w-6 tw-h-fit">
          <SaveIcon fill={isRecipeSaved ? 'currentColor' : ''} />
        </div>
        <p className="tra-text-web-utl-button-m tw-mb-0 tw-ml-2 tw-tracking-normal">
          {!user && !isLoading
            ? t('recipeContent.loginToSaveLabel', undefined, { default: 'Login to Save Recipe' })
            : isRecipeSaved
              ? t('recipeContent.unsaveLabel', undefined, { default: 'Remove from Saved Recipes' })
              : t('recipeContent.saveLabel', undefined, { default: 'Save Recipe To Traeger App' })}
        </p>
      </div>
    </Button>
  );
};

export default SaveButton;
